import { useCallback } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { BrandPlan, ContentAccounts, ContentStrategy, ContentStrategyType } from 'types'
import { deriveInitialCountsFromStrategy, deriveInitialSettingsFromCounts } from './contentTypeDefaultsUtils'
import { useCreateCampaignMachine } from './useCreateCampaignMachine'
import { StateId, EventId, Campaign, CampaignSource, ContentTypeSettingsMap } from './Types'
import { StrategyAndContextSelection } from './StrategyAndContextSelection'
import { ContentTypeDefaults } from './ContentTypeDefaults'
import { CampaignDetails } from './CampaignDetails'
import Styles from './CreateCampaignModalV2.module.scss'

interface CreateCampaignModalV2Props {
  brandPlan: BrandPlan
  onSuccess?: () => void
}

export const CreateCampaignModalV2 = ({ brandPlan, onSuccess }: CreateCampaignModalV2Props) => {
  const [current, send] = useCreateCampaignMachine({ brandPlan, onSuccess })
  const { closeModal } = useModals()
  const isStrictBrandControl = brandPlan.brand_kit?.strict_brand_control ?? false

  let title = 'Create Campaign'
  if (current.matches(StateId.ReviewDetails) || current.matches(StateId.Creating)) title = 'Review campaign details'
  if (current.matches(StateId.ContentTypeDefaults)) title = 'Content per week'

  const handleNext = useCallback(
    (
      strategyType: ContentStrategyType,
      strategyConfig: ContentStrategy,
      sources: CampaignSource[],
      themeDetails: string | null,
    ) => {
      send({ type: EventId.Next, strategyType, strategyConfig, sources, themeDetails })
    },
    [send],
  )

  const handleContentTypesConfirm = useCallback(
    (settings: ContentTypeSettingsMap, contentAccounts?: ContentAccounts) => {
      send({ type: EventId.SetContentTypes, settings, contentAccounts })
    },
    [send],
  )

  const handleCampaignDetailsConfirm = useCallback(
    (campaign: Campaign) => {
      send({ type: EventId.SetCampaignDetails, campaign })
    },
    [send],
  )

  const handleBack = useCallback(() => {
    send({ type: EventId.Back })
  }, [send])

  const handleContentTypeDefaultsBack = useCallback(
    (settings: ContentTypeSettingsMap, contentAccounts?: ContentAccounts) => {
      requestAnimationFrame(() => send({ type: EventId.SetContentTypes, settings, contentAccounts }))
    },
    [send],
  )

  const handleCancel = useCallback(() => {
    closeModal()
  }, [closeModal])

  const handleCreate = useCallback(
    (campaign: Campaign) => {
      send({ type: EventId.Create, campaign })
    },
    [send],
  )

  const handleEditContentTypes = useCallback(() => {
    send({ type: EventId.SetContentTypes })
  }, [send])

  const isCreating = current.matches(StateId.Creating)
  const showDetails = current.matches(StateId.ReviewDetails) || isCreating
  const showContentTypeDefaults = current.matches(StateId.ContentTypeDefaults)
  return (
    <Modal.Root bodyClassName={Styles.body}>
      <Modal.Header title={title} />
      {current.matches(StateId.StrategyAndContextSelection) && (
        <StrategyAndContextSelection
          initialSources={current.context.campaign?.sources ?? []}
          initialThemeDetails={current.context.themeDetails}
          brandPlan={brandPlan}
          onNext={handleNext}
          onCancel={handleCancel}
        />
      )}
      {showContentTypeDefaults && (
        <ContentTypeDefaults
          initialSettings={
            current.context.contentTypeSettings ??
            deriveInitialSettingsFromCounts(
              deriveInitialCountsFromStrategy(current.context.strategyConfig),
              current.context.campaign?.weekDays ?? brandPlan.week_days,
            )
          }
          initialContentAccounts={current.context.campaign?.content_accounts}
          defaultWeekDays={current.context.campaign?.weekDays ?? brandPlan.week_days}
          onConfirm={handleContentTypesConfirm}
          onBack={handleContentTypeDefaultsBack}
          isStrictBrandControl={isStrictBrandControl}
        />
      )}
      {showDetails && (
        <CampaignDetails
          campaign={current.context.campaign}
          brandPlan={brandPlan}
          onBack={handleBack}
          onCreate={handleCreate}
          onCampaignUpdated={handleCampaignDetailsConfirm}
          isSubmitting={isCreating}
          contentTypeSettings={
            current.context.contentTypeSettings ??
            deriveInitialSettingsFromCounts(
              deriveInitialCountsFromStrategy(current.context.strategyConfig),
              current.context.campaign?.weekDays ?? brandPlan.week_days,
            )
          }
          onEditContentTypes={handleEditContentTypes}
          themeDetails={current.context.themeDetails}
        />
      )}
    </Modal.Root>
  )
}
